/**
 * @fileoverview Declarative, attribute-driven cookie-consent banner (no-code).
 * @module consent/declarativeBanner
 *
 * @description
 * The no-code front door to the FlowGrid consent stack. A customer drops a
 * single element on the page:
 *
 * ```html
 * <div data-cookie-ccbanner></div>
 * ```
 *
 * …and this layer initialises a fully styled, GDPR/CCPA-friendly consent banner.
 * It defaults to opt-out (nothing is blocked — FlowGrid collects data unless the
 * visitor declines); set `data-cc-mode="opt-in"` to block analytics, marketing and
 * session-replay tracking until the visitor accepts — with zero JavaScript. It
 * contains **no consent logic of its
 * own**: it reads config off the page and delegates to the shipped engine
 * ({@link renderConsentBanner} for the default styled banner, or
 * {@link attachConsentBanner} when the customer supplied their own inner markup),
 * a self-contained declarative layer: markup in, behaviour out, no author JS.
 *
 * @attributes
 *   data-cookie-ccbanner            Marker. Presence = initialise a banner here.
 *   data-cc-mode                    "opt-out" (default) | "opt-in".
 *   data-cc-title / -description    Copy overrides.
 *   data-cc-accept-label / ...      Button label overrides.
 *   data-cc-privacy-url             Adds a "Learn more" privacy link.
 *   data-cc-position / -primary /   Theme: position, colours, radius, font, z.
 *     -bg / -text / -radius / -font / -z
 *   data-cc-categories              Comma list of visible toggles (analytics,marketing).
 *   data-cc-cookie-name / -days /   Cookie persistence overrides.
 *     -domain
 *   data-cc-respect-dnt="false"     Ignore DNT/GPC (defaults to honouring it).
 *   data-cc-theme                   Named preset bundle: flowgrid | light | dark.
 *   data-cc-unstyled                Drop default CSS (Tier 2 BYO-CSS).
 *   data-cc-manage-selector         CSS selector whose clicks re-open the banner.
 *   data-cc-manual                  Skip auto-init for this element.
 *
 * Robust to dynamically-injected DOM: a MutationObserver re-scans on changes, so
 * page builders and SPA route changes keep working with zero extra calls. Init
 * is idempotent per element via a `data-cc-wired` marker.
 */

import { renderConsentBanner, attachConsentBanner } from "../helpers/consent";
import type { RenderConsentBannerOptions, ConsentBannerHandle } from "../helpers/consent";
import type { CookieBannerTheme } from "./types";

const MARKER_ATTR = "data-cookie-ccbanner";
const WIRED_ATTR = "data-cc-wired";
const MANUAL_ATTR = "data-cc-manual";

/**
 * Named theme presets reachable from `data-cc-theme` / `theme: "<name>"`.
 * Explicit `data-cc-*` colour attributes still override the preset.
 */
const THEME_PRESETS: Record<string, Partial<CookieBannerTheme>> = {
  flowgrid: { primaryColor: "#4F46E5", backgroundColor: "#FFFFFF", textColor: "#1F2937" },
  light: { primaryColor: "#4F46E5", backgroundColor: "#FFFFFF", textColor: "#1F2937" },
  dark: { primaryColor: "#818CF8", backgroundColor: "#111827", textColor: "#F9FAFB" },
};

/** Parse a numeric attribute, returning `undefined` when absent or invalid. */
function num(raw: string | null): number | undefined {
  if (raw == null || raw.trim() === "") return undefined;
  const n = Number(raw);
  return Number.isFinite(n) ? n : undefined;
}

/**
 * Read `data-cc-*` attributes off an element into a partial banner config.
 * Only present attributes are emitted, so this object can be layered over
 * `window.FlowGridConfig.consentBanner` without clobbering it with defaults.
 */
function parseElementConfig(el: Element): RenderConsentBannerOptions {
  const attr = (name: string): string | null => el.getAttribute(name);
  const cfg: RenderConsentBannerOptions = {};
  const theme: CookieBannerTheme = {};

  // Named preset first so explicit colour attributes below override it.
  const preset = attr("data-cc-theme");
  if (preset && THEME_PRESETS[preset]) Object.assign(theme, THEME_PRESETS[preset]);

  const mode = attr("data-cc-mode");
  if (mode === "opt-in" || mode === "opt-out") cfg.mode = mode;

  const title = attr("data-cc-title");
  if (title != null) cfg.title = title;
  const description = attr("data-cc-description");
  if (description != null) cfg.description = description;
  const acceptLabel = attr("data-cc-accept-label");
  if (acceptLabel != null) cfg.acceptAllLabel = acceptLabel;
  const rejectLabel = attr("data-cc-reject-label");
  if (rejectLabel != null) cfg.rejectAllLabel = rejectLabel;
  const customizeLabel = attr("data-cc-customize-label");
  if (customizeLabel != null) cfg.customizeLabel = customizeLabel;
  const saveLabel = attr("data-cc-save-label");
  if (saveLabel != null) cfg.saveLabel = saveLabel;
  const privacyUrl = attr("data-cc-privacy-url");
  if (privacyUrl != null) cfg.privacyPolicyUrl = privacyUrl;

  // Theme appearance
  const position = attr("data-cc-position") as CookieBannerTheme["position"] | null;
  if (position) theme.position = position;
  const primary = attr("data-cc-primary");
  if (primary != null) theme.primaryColor = primary;
  const bg = attr("data-cc-bg");
  if (bg != null) theme.backgroundColor = bg;
  const text = attr("data-cc-text");
  if (text != null) theme.textColor = text;
  const radius = attr("data-cc-radius");
  if (radius != null) theme.borderRadius = radius;
  const font = attr("data-cc-font");
  if (font != null) theme.fontFamily = font;
  const z = num(attr("data-cc-z"));
  if (z !== undefined) theme.zIndex = z;
  if (Object.keys(theme).length > 0) cfg.theme = theme;

  // Categories (comma list of visible non-essential toggles).
  const categories = attr("data-cc-categories");
  if (categories != null) {
    cfg.categories = categories
      .split(",")
      .map((s) => s.trim())
      .filter((s): s is "analytics" | "marketing" | "preferences" | "necessary" => s.length > 0);
  }

  // Cookie persistence (ConsentConfig fields).
  const cookieName = attr("data-cc-cookie-name");
  if (cookieName != null) cfg.cookieName = cookieName;
  const cookieDays = num(attr("data-cc-cookie-days"));
  if (cookieDays !== undefined) cfg.cookieDays = cookieDays;
  const cookieDomain = attr("data-cc-cookie-domain");
  if (cookieDomain != null) cfg.cookieDomain = cookieDomain;
  const respectDNT = attr("data-cc-respect-dnt");
  if (respectDNT != null) cfg.respectDNT = respectDNT !== "false";

  if (el.hasAttribute("data-cc-unstyled")) cfg.unstyled = true;
  const manageSelector = attr("data-cc-manage-selector");
  if (manageSelector != null) cfg.manageSelector = manageSelector;

  return cfg;
}

/**
 * Merge global config (`window.FlowGridConfig.consentBanner`) with the
 * per-element `data-cc-*` config. Precedence (low → high, per the plan):
 *   1. global object
 *   2. element attributes
 * `theme` is merged field-by-field so an element can tweak one colour without
 * dropping the rest of the global theme.
 */
function mergeConfig(
  global: RenderConsentBannerOptions | undefined,
  element: RenderConsentBannerOptions,
): RenderConsentBannerOptions {
  if (!global) return element;
  return {
    ...global,
    ...element,
    theme: global.theme || element.theme ? { ...global.theme, ...element.theme } : undefined,
  };
}

/** Whether an element already contains author markup (Tier 3 BYO-markup). */
function hasChildMarkup(el: Element): boolean {
  return el.children.length > 0;
}

/** Options for {@link wireDeclarativeBanners}. */
export interface WireDeclarativeBannersOptions {
  /**
   * Global defaults, normally read from `window.FlowGridConfig.consentBanner`.
   * Per-element `data-cc-*` attributes override these.
   */
  globalConfig?: RenderConsentBannerOptions;
}

/** Handle returned by {@link wireDeclarativeBanners}. */
export interface DeclarativeBannersHandle {
  /** Re-scan the DOM for new `[data-cookie-ccbanner]` markers. Idempotent. */
  rescan: () => void;
  /** Stop the MutationObserver and tear down every wired banner. */
  disconnect: () => void;
}

/**
 * Scan the document for `[data-cookie-ccbanner]` markers and initialise a
 * consent banner for each: the default styled banner via {@link renderConsentBanner}
 * when the element is empty, or the customer's own markup via
 * {@link attachConsentBanner} when it has children. Watches for late-mounted
 * markers via a MutationObserver. Each element is marked `data-cc-wired` so
 * re-scans never double-init.
 *
 * SSR-safe: a no-op (returns inert handle) when there's no `document`.
 *
 * @param options - Optional global defaults to layer beneath `data-cc-*`.
 * @returns A {@link DeclarativeBannersHandle} for manual rescans / teardown.
 */
export function wireDeclarativeBanners(options?: WireDeclarativeBannersOptions): DeclarativeBannersHandle {
  if (typeof document === "undefined") {
    return { rescan: () => {}, disconnect: () => {} };
  }

  const globalConfig = options?.globalConfig;
  const handles: ConsentBannerHandle[] = [];

  const wireElement = (el: Element) => {
    if (el.hasAttribute(WIRED_ATTR) || el.hasAttribute(MANUAL_ATTR)) return;
    el.setAttribute(WIRED_ATTR, "");

    const config = mergeConfig(globalConfig, parseElementConfig(el));

    let handle: ConsentBannerHandle | null = null;
    try {
      if (hasChildMarkup(el)) {
        // Tier 3 — bind the customer's own buttons by data-role, never inject DOM.
        handle = attachConsentBanner({ ...config, container: el as HTMLElement });
      } else {
        // Tiers 1 & 2 — render the default styled banner (the marker is just an
        // anchor; the banner is position:fixed so DOM placement doesn't matter).
        handle = renderConsentBanner(config);
      }
    } catch {
      /* never throw from auto-init — leave the page untouched on failure */
    }
    if (handle) handles.push(handle);
  };

  const scan = (root: ParentNode = document) => {
    const sel = `[${MARKER_ATTR}]:not([${WIRED_ATTR}])`;
    // The root node itself may be a marker (e.g. a single injected element).
    if (root instanceof Element && root.matches(sel)) wireElement(root);
    root.querySelectorAll(sel).forEach(wireElement);
  };

  scan();

  // Re-scan when the DOM changes (page builders, SPA navigation, lazy content).
  const observer =
    typeof MutationObserver !== "undefined"
      ? new MutationObserver((mutations) => {
          for (const m of mutations) {
            m.addedNodes.forEach((node) => {
              if (node.nodeType === 1) scan(node as Element);
            });
          }
        })
      : null;
  observer?.observe(document.documentElement, { childList: true, subtree: true });

  return {
    rescan: () => scan(),
    disconnect: () => {
      try {
        observer?.disconnect();
      } catch {
        /* ignore */
      }
      for (const h of handles) {
        try {
          h.destroy();
        } catch {
          /* ignore */
        }
      }
      handles.length = 0;
    },
  };
}
